import React, { ComponentProps, useEffect, useState } from 'react'
import { inject } from 'mobx-react'
import { observer } from 'mobx-react-lite'
import { Button, Modal, Skeleton } from 'antd-v4'
import { ModalProps } from 'antd-v4/lib/modal'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { ConfigureEcomDelivery } from './ConfigureEcomDelivery'
import { ConfigureEcomDeliveryRadioForm } from './ConfigureEcomDeliveryRadioForm'
import { ConfigureEcomDeliveryDropDownForm } from './ConfigureEcomDeliveryDropDownForm'
import './styles.scss'
import { LocationWithConfig } from './EcommerceSettingModels'
import { LOCATION_ZONE_OPTION, updateStore } from '@getgreenline/blaze-ecomm'
import Endpoints from '../../../../constants/Endpoints'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'

interface Props {
  visible: boolean
  onCloseModal: () => void
  configureEcomDelivery: ConfigureEcomDelivery
  setSelectedLocation: (location: LocationWithConfig) => void
  isDeliveryLoading: boolean
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

const _ConfigureEcomDeliveryModal = observer(
  ({
    currentCompanyStore,
    visible,
    onCloseModal,
    configureEcomDelivery,
    setSelectedLocation,
    isDeliveryLoading,
  }: Props & InjectedProps) => {
    const [flatRateFee, setFlatRateFee] = useState<number>(0)

    const updateFlatRateFee = (flatRateFee: number) => {
      // Handling updated API to update flat rate fee
      if (flatRateFee && !configureEcomDelivery.flatRateObject) {
        configureEcomDelivery.addLocationZone({
          locationZoneOption: LOCATION_ZONE_OPTION.FLAT_RATE,
          name: configureEcomDelivery.FLAT_RATE_NAME,
          postalCodes: ['*'],
        })
      }

      // Handling LEGACY flat rate with dummy postalCodes
      if (configureEcomDelivery.flatRateObject) {
        const updatedFlatRateObject = {
          ...configureEcomDelivery.flatRateObject,
          fee: flatRateFee || 0,
        }

        const updateLocationZones = [
          updatedFlatRateObject,
          ...configureEcomDelivery.locationZonesToShow,
        ]

        configureEcomDelivery.setAllLocationZones(updateLocationZones)
      }

      setFlatRateFee(flatRateFee)
    }

    const confirmAndClose = () => {
      const closeModalAndResetKeys = () => {
        onCloseModal()
        configureEcomDelivery.reset()
      }

      if (configureEcomDelivery.isLocationZonesModified) {
        Modal.confirm({
          title: 'Are you sure you want to leave?',
          icon: <ExclamationCircleOutlined translate='yes' />,
          zIndex: 1002,
          content: "Remember to save any changes you've made before you proceed",
          okText: 'Discard Changes',
          okButtonProps: { type: 'primary', danger: true },
          cancelText: 'Go Back & Save',
          centered: true,
          onOk: closeModalAndResetKeys,
        })
      } else {
        closeModalAndResetKeys()
      }
    }

    const confirmAndSubmit = () => {
      const {
        canUseKMLFiles,
        config: locationWithConfig,
        isUsingLegacyLocationZones,
        hasOtherLocationZones,
        locationZonesToShow,
        saveLocationZones,
        locationZoneOption,
      } = configureEcomDelivery

      const shouldPopupWarningModal =
        !canUseKMLFiles && !isUsingLegacyLocationZones && hasOtherLocationZones

      const shouldDisableDelivery =
        canUseKMLFiles &&
        locationZoneOption !== LOCATION_ZONE_OPTION.FLAT_RATE &&
        locationZonesToShow.length === 0

      const isEcommDeliveryEnabled = locationWithConfig.isEcommDeliveryEnabled

      const disableDelivery = async () => {
        if (!isEcommDeliveryEnabled) return

        const ecomConfig = locationWithConfig.config

        if (!ecomConfig) {
          throw new Error('No ECOM config found')
        }

        await updateStore(
          Endpoints.AUTHZ_GATEWAY_URL,
          ecomConfig.greenlineAttributes.tenantId,
          ecomConfig.greenlineAttributes.retailerId,
          {
            isActive: ecomConfig.attributes.isActive,
            allowDeliveries: false,
          },
        )
        await currentCompanyStore?.editLocation({
          id: locationWithConfig.id,
          isEcommDeliveryEnabled: false,
        })

        setSelectedLocation({
          ...locationWithConfig,
          isEcommDeliveryEnabled: false,
        })

        onCloseModal()
      }

      const save = async () => {
        await saveLocationZones(shouldDisableDelivery ? disableDelivery : undefined)
      }

      const warningMessage = shouldDisableDelivery ? (
        <div>
          There are no zones added.
          <br />
          {isEcommDeliveryEnabled && (
            <>
              Saving will disable delivery for this location.
              <br />
            </>
          )}
          Are you sure you want to proceed?
        </div>
      ) : (
        <div>
          You have multiple options set.
          <br />
          Other options will be deleted if you save with
          {' ' + locationZoneOption}
          .<br />
          Are you sure you want to proceed?
        </div>
      )

      if (shouldPopupWarningModal || shouldDisableDelivery) {
        Modal.confirm({
          title: 'Are you sure you want to save?',
          icon: <ExclamationCircleOutlined translate='yes' />,
          zIndex: 1002,
          content: warningMessage,
          okText: 'Save',
          okButtonProps: { type: 'primary', danger: true },
          cancelText: 'Cancel',
          centered: true,
          onOk: save,
        })
      } else {
        save()
      }
    }

    useEffect(() => {
      if (!visible) {
        configureEcomDelivery.reset()
      }

      updateFlatRateFee(configureEcomDelivery.flatRateObject?.fee || 0)
    }, [visible, configureEcomDelivery.isLoading])

    const footerContent = [
      <Button key='cancel' onClick={confirmAndClose}>
        Cancel
      </Button>,
      <Button
        key='submit'
        type='primary'
        loading={configureEcomDelivery.isLoading || configureEcomDelivery.isSaving}
        onClick={confirmAndSubmit}
        disabled={
          !configureEcomDelivery.isLocationZonesModified ||
          configureEcomDelivery.isLoading ||
          configureEcomDelivery.isSaving
        }
      >
        Save
      </Button>,
    ]

    const modalProps: ModalProps = {
      zIndex: 1001,
      width: '72%',
      className: 'configure-ecom-delivery-modal-reponsive',
      title: 'Configure Delivery Settings',
      visible: visible,
      onCancel: confirmAndClose,
      maskClosable: false,
      footer: footerContent,
      centered: true,
    }

    if (isDeliveryLoading || configureEcomDelivery.isLoading) {
      return (
        <Modal {...modalProps}>
          <Skeleton loading={configureEcomDelivery.isLoading} />
        </Modal>
      )
    }

    const deliveryProps: ComponentProps<typeof ConfigureEcomDeliveryDropDownForm> = {
      configureEcomDelivery,
      flatRateFee,
      updateFlatRateFee,
    }

    return (
      <Modal {...modalProps}>
        {configureEcomDelivery.canUseKMLFiles ? (
          <ConfigureEcomDeliveryDropDownForm {...deliveryProps} />
        ) : (
          <ConfigureEcomDeliveryRadioForm {...deliveryProps} visible={visible} />
        )}
      </Modal>
    )
  },
)

// Usually wrapping the component with both inject and observer methods, but in this case, observer() is already done to reduce changes.
export const ConfigureEcomDeliveryModal = inject('currentCompanyStore')(_ConfigureEcomDeliveryModal)
